// Per-dialect pseudo-table registry. "Pseudo-tables" are identifiers that
// look like table references but aren't real tables in a schema catalog —
// they're provided by the SQL engine's execution context. Examples:
//   - MSSQL: `inserted` / `deleted` / `$action` inside OUTPUT and MERGE
//   - PG / SQLite: `excluded` inside ON CONFLICT DO UPDATE SET
//   - PG / MySQL / Oracle / SQLite: `NEW` / `OLD` row-alias in trigger bodies
//     (delegated to the procedural-body resolver; this module records only
//     the names + outer-context gating hints).
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver —
// subqueries + pseudo-tables + resolveScope assembly.
//
// Usage pattern: `collectPseudoTablesForContext(engine, context)` returns the
// AliasBinding-shaped list to inject into the current Scope. The caller
// (resolveScope) supplies the `context` string it inferred from the
// surrounding SQL — the classifier or a light scanner decides whether the
// cursor is inside MERGE WHEN, OUTPUT, ON CONFLICT, or a trigger body.

import type { AliasBinding, AliasSource, SqlRange } from '../types';
import type { SqlEngine, QualifiedName } from '../../sql-grammar/model';
import { foldIdentifier } from './identifiers';

// Context string the resolver passes in. Keep this set tight so the
// pseudo-table list stays deterministic across dialects.
export type PseudoTableContext =
  | 'merge-when-matched'
  | 'merge-when-not-matched-target'
  | 'merge-when-not-matched-source'
  | 'output'
  | 'on-conflict-do-update'
  | 'trigger-body'
  | 'insert-duplicate-key-update'
  | 'none';

const ZERO_RANGE: SqlRange = {
  startOffset: 0, endOffset: 0,
  startLine: 0, startColumn: 0, endLine: 0, endColumn: 0,
};

function pseudo(name: string, foldedName: string): AliasBinding {
  const target: QualifiedName = { name, raw: name, quoted: false, foldedName };
  return {
    alias: name,
    aliasFolded: foldedName,
    target,
    source: 'pseudo' as AliasSource,
    range: ZERO_RANGE,
  };
}

// Folded-name policy: pseudo-table display names are engine-defined keywords,
// but their FOLDED lookup key must use the engine's own identifier fold so it
// can match an engine-folded reference in findReachableTable. Oracle folds
// unquoted identifiers to UPPER; postgres to lower; mssql/mysql/sqlite are
// case-insensitive (lowercased). Computing the folded key via foldIdentifier
// keeps the stored key consistent with how dotted references are folded.
export function collectPseudoTablesForContext(
  engine: SqlEngine,
  context: PseudoTableContext,
): AliasBinding[] {
  if (context === 'none') return [];

  // Engine-correct fold for unquoted pseudo names (SRO-3). defaultFolding(engine)
  // is applied internally by foldIdentifier when identifierFolding is unset.
  const fold = (n: string): string => foldIdentifier(n, false, { engine });

  if (context === 'merge-when-matched'
    || context === 'merge-when-not-matched-target'
    || context === 'merge-when-not-matched-source'
    || context === 'output') {
    if (engine === 'mssql') {
      return [pseudo('inserted', fold('inserted')), pseudo('deleted', fold('deleted'))];
    }
    return [];
  }

  if (context === 'on-conflict-do-update') {
    if (engine === 'postgres' || engine === 'sqlite') {
      return [pseudo('excluded', fold('excluded'))];
    }
    return [];
  }

  if (context === 'insert-duplicate-key-update') {
    // MySQL 8.0.19+ syntax: `INSERT ... AS new ON DUPLICATE KEY UPDATE ...`.
    // The pseudo-name is user-chosen (`new` by convention); engines without
    // the AS alias expose `NEW.col` + `VALUES(col)`. We don't know the
    // user-chosen alias here, so return just the conventional NEW row alias.
    if (engine === 'mysql') {
      return [pseudo('NEW', fold('NEW')), pseudo('OLD', fold('OLD'))];
    }
    return [];
  }

  if (context === 'trigger-body') {
    // Trigger-body pseudo-tables are engine-specific. PL/pgSQL's NEW/OLD are
    // variables not tables; the per-engine procedural-locals path
    // (intellisense/resolvers/*ProceduralLocals.ts) owns them. For the
    // outer SR surface we expose the names but mark their targets as
    // pseudo — downstream consumers that need the actual row shape read it
    // from there (the ProceduralResolver registry this comment used to name
    // was deleted as dead code; see proceduralResolver.ts, plan16 T10).
    if (engine === 'mssql') return [pseudo('inserted', fold('inserted')), pseudo('deleted', fold('deleted'))];
    if (engine === 'postgres' || engine === 'sqlite') return [pseudo('NEW', fold('NEW')), pseudo('OLD', fold('OLD'))];
    if (engine === 'mysql') return [pseudo('NEW', fold('NEW')), pseudo('OLD', fold('OLD'))];
    if (engine === 'oracle') return [pseudo(':NEW', fold(':NEW')), pseudo(':OLD', fold(':OLD'))];
  }

  return [];
}

// Convenience: is a bare identifier one of the engine's pseudo-table names
// in ANY context? Used by resolveIdentifier's fallback pass when the
// surrounding context wasn't inferred (e.g. suggest-table callers passing
// partial buffers). Intentionally permissive — a false positive here just
// means the caller may still need to verify via `collectPseudoTablesForContext`.
export function isKnownPseudoTableName(engine: SqlEngine, nameFolded: string): boolean {
  // SRO-3 fold hygiene: callers pass an already engine-folded name, so compare
  // it against the SAME engine fold of each pseudo literal — never re-fold via
  // .toLowerCase() (which only matched Oracle by the coincidence that
  // lowercasing the upper-folded `:NEW` reproduces `:new`). Oracle folds
  // unquoted names UPPER (`:NEW`/`:OLD`), pg/sqlite/mysql lower, mssql lower —
  // foldIdentifier(name,false,{engine}) yields each engine's correct basis.
  const fold = (n: string): string => foldIdentifier(n, false, { engine });
  if (engine === 'mssql') {
    return nameFolded === fold('inserted') || nameFolded === fold('deleted') || nameFolded === fold('$action');
  }
  if (engine === 'postgres' || engine === 'sqlite') {
    return nameFolded === fold('excluded') || nameFolded === fold('new') || nameFolded === fold('old');
  }
  if (engine === 'mysql') return nameFolded === fold('new') || nameFolded === fold('old');
  if (engine === 'oracle') return nameFolded === fold(':NEW') || nameFolded === fold(':OLD');
  return false;
}
